import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    });

    document.addEventListener('click', this.#handleDocumentClick, {
      signal: this.#abortController.signal,
    });

    document.addEventListener('keydown', this.#handleDocumentKeydown, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = findListItem(event.target);

    // Shop / Collections: jamás se activan por hover/focus/eventos indirectos.
    if (listItem?.classList.contains('menu-list__list-item--click-only')) {
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const previouslyActiveItem = this.#state.activeItem;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowHovered && !force) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150));
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(() => this.#deactivate(), DEACTIVATE_DELAY);

  /**
   * Handle clicks on click-only menu items (Shop / Collections)
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const menuItem = findMenuItem(event.target);
    const listItem = findListItem(event.target);
    const directInteractiveTarget = event.target.closest('[ref="menuitem"], a, button');

    if (!menuItem || !listItem?.classList.contains('menu-list__list-item--click-only')) return;
    if (!directInteractiveTarget) return;

    event.preventDefault();
    event.stopPropagation();

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.removeAttribute('data-animating');

    if (this.#state.activeItem === menuItem && menuItem.ariaExpanded === 'true') {
      this.#deactivate(menuItem, true);
      return;
    }

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== menuItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = menuItem;
    this.ariaExpanded = 'true';
    menuItem.ariaExpanded = 'true';
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(menuItem);
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Close menu when clicking outside
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Close menu on Escape
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  return /** @type {HTMLElement | null} */ (element.closest('[ref="menuitem"]'));
}

/**
 * Find the closest list item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findListItem(element) {
  if (!(element instanceof Element)) return null;
  return /** @type {HTMLElement | null} */ (element.closest('.menu-list__list-item'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = findListItem(element);
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}